import React, { Component } from 'react';
import PropTypes from 'prop-types';
import GiftListConfigureWapModal from '../../../wapComponents/GiftListConfigureWapModal';
import commonUtil from '../../../../../../../utils/commonUtil';
import {
  CAMERA_IMAGE_SIZE,
  normalizeImageToSizeRange,
} from './cameraImageUtil';

/**
 * CameraCaptureModal
 *
 * Lets the user take a photo using the device camera (laptop webcam or mobile
 * camera) via the native getUserMedia API. The captured frame is handed back to
 * the parent as a JPEG `File` through `onCapture`, so it can be routed through
 * the exact same upload pipeline (GCPCloudImageUploader.handleFileSelect) used
 * by the file/gallery upload flows.
 */
class CameraCaptureModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // 'requesting' | 'streaming' | 'captured' | 'denied' | 'error'
      status: 'requesting',
      capturedUrl: '',
      errorMessage: '',
    };
    this.videoRef = React.createRef();
    this.stream = null;
    this.capturedBlob = null;
  }

  componentDidMount() {
    this.startCamera();
  }

  componentWillUnmount() {
    this.stopStream();
    this.revokeCapturedUrl();
  }

  stopStream = () => {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  };

  revokeCapturedUrl = () => {
    if (this.state.capturedUrl) {
      try {
        URL.revokeObjectURL(this.state.capturedUrl);
      } catch (e) {
        // no-op
      }
    }
  };

  startCamera = async () => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      this.setState({
        status: 'error',
        errorMessage: 'Tu navegador no soporta el acceso a la cámara.',
      });
      return;
    }

    this.setState({ status: 'requesting', errorMessage: '' });

    // Optional pre-check. Not supported in every browser, so failures are ignored.
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permission = await navigator.permissions.query({
          name: 'camera',
        });
        if (permission && permission.state === 'denied') {
          this.setState({
            status: 'denied',
            errorMessage:
              'Permite el acceso a la cámara en la configuración de tu navegador para continuar.',
          });
          return;
        }
      }
    } catch (e) {
      // Permissions API unavailable for 'camera' (Firefox/Safari) — fall through to getUserMedia.
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      this.stream = stream;
      this.setState({ status: 'streaming' }, () => {
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream;
        }
      });
    } catch (error) {
      this.handleCameraError(error);
    }
  };

  handleCameraError = (error) => {
    const name = error && error.name;
    if (
      name === 'NotAllowedError' ||
      name === 'SecurityError' ||
      name === 'PermissionDeniedError'
    ) {
      this.setState({
        status: 'denied',
        errorMessage:
          'Permite el acceso a la cámara en la configuración de tu navegador para continuar.',
      });
    } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      this.setState({
        status: 'error',
        errorMessage: 'No se encontró una cámara en tu dispositivo.',
      });
    } else if (name === 'NotReadableError' || name === 'TrackStartError') {
      this.setState({
        status: 'error',
        errorMessage: 'La cámara está siendo utilizada por otra aplicación.',
      });
    } else {
      this.setState({
        status: 'error',
        errorMessage:
          'No fue posible acceder a la cámara. Inténtalo nuevamente.',
      });
    }
  };

  takePhoto = () => {
    const video = this.videoRef.current;
    if (!video) return;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    const onBlobReady = (blob) => {
      if (!blob) {
        this.setState({
          status: 'error',
          errorMessage:
            'No fue posible capturar la foto. Inténtalo nuevamente.',
        });
        return;
      }
      // Release the camera as soon as we have the frame.
      this.stopStream();
      this.revokeCapturedUrl();
      this.capturedBlob = blob;
      const capturedUrl = URL.createObjectURL(blob);
      this.setState({ status: 'captured', capturedUrl });
    };

    if (CAMERA_IMAGE_SIZE.enabled) {
      // Normalize the captured image so its file size lands within the upload
      // limits (128 KB – 3 MB) before it reaches GCPCloudImageUploader.validateFile.
      normalizeImageToSizeRange(canvas, CAMERA_IMAGE_SIZE)
        .then(onBlobReady)
        .catch(() => {
          // Fallback to a plain encode if normalization fails for any reason.
          canvas.toBlob(onBlobReady, 'image/jpeg', 0.92);
        });
    } else {
      // Toggle OFF: previous behavior (raw capture, subject to the existing validation).
      canvas.toBlob(onBlobReady, 'image/jpeg', 0.92);
    }
  };

  retake = () => {
    this.revokeCapturedUrl();
    this.capturedBlob = null;
    this.setState({ capturedUrl: '' });
    this.startCamera();
  };

  usePhoto = () => {
    if (!this.capturedBlob) return;
    const file = new File([this.capturedBlob], 'camera-capture.jpg', {
      type: 'image/jpeg',
    });
    this.props.onCapture(file);
    this.handleClose();
  };

  handleClose = () => {
    this.stopStream();
    this.revokeCapturedUrl();
    this.props.onClose();
  };

  renderBody = () => {
    const { status, capturedUrl, errorMessage } = this.state;

    return (
      <div className='camera-capture-body'>
        {(status === 'requesting' || status === 'streaming') && (
          <div className='camera-capture-stage'>
            <video
              ref={this.videoRef}
              className='camera-capture-video'
              autoPlay
              playsInline
              muted
            />
            {status === 'requesting' && (
              <div className='camera-capture-hint'>Accediendo a la cámara…</div>
            )}
          </div>
        )}

        {status === 'captured' && (
          <div className='camera-capture-stage'>
            <img
              className='camera-capture-preview'
              src={capturedUrl}
              alt='Foto capturada'
            />
          </div>
        )}

        {(status === 'denied' || status === 'error') && (
          <div className='camera-capture-message'>
            <img
              src={commonUtil.getAssetURL() + '/images/camera_alt_black.svg'}
              alt='Cámara'
            />
            <p>{errorMessage}</p>
          </div>
        )}

        <div className='camera-capture-actions'>
          {status === 'streaming' && (
            <button
              type='button'
              className='camera-capture-btn camera-capture-btn-primary'
              onClick={this.takePhoto}
            >
              Capturar
            </button>
          )}
          {status === 'captured' && (
            <>
              <button
                type='button'
                className='camera-capture-btn camera-capture-btn-secondary'
                onClick={this.retake}
              >
                Repetir
              </button>
              <button
                type='button'
                className='camera-capture-btn camera-capture-btn-primary'
                onClick={this.usePhoto}
              >
                Usar foto
              </button>
            </>
          )}
          {status === 'denied' && (
            <button
              type='button'
              className='camera-capture-btn camera-capture-btn-primary'
              onClick={this.startCamera}
            >
              Reintentar
            </button>
          )}
          {status === 'error' && (
            <button
              type='button'
              className='camera-capture-btn camera-capture-btn-primary'
              onClick={this.startCamera}
            >
              Reintentar
            </button>
          )}
        </div>
      </div>
    );
  };

  render() {
    if (this.props.isWap) {
      return (
        <GiftListConfigureWapModal
          headerClassName='evnt-modal-header-giftList'
          titleClassName='evnt-modal-title-giftList'
          closeIconClassName='evnt-modal-close-icon-giftList'
          title={this.props.title}
          bodyClassName='evnt-modal-body-giftList camera-capture-modal'
          closeModal={this.handleClose}
        >
          {this.renderBody()}
        </GiftListConfigureWapModal>
      );
    }

    return (
      <div className='camera-capture-overlay'>
        <div className='camera-capture-content'>
          <div className='camera-capture-header'>
            <h4 className='camera-capture-title'>{this.props.title}</h4>
            <button
              type='button'
              className='camera-capture-close'
              onClick={this.handleClose}
              aria-label='Cerrar'
            >
              <img
                src={commonUtil.getAssetURL() + '/images/close.svg'}
                alt=''
              />
            </button>
          </div>
          {this.renderBody()}
        </div>
      </div>
    );
  }
}

CameraCaptureModal.propTypes = {
  isWap: PropTypes.bool,
  title: PropTypes.string,
  onCapture: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CameraCaptureModal;
